import { ChemicalResistance } from '@igus/material-domain';
import type { NbsResponse } from './uploadFile/fetchNbsRecommendation';
import { type Material, type Procedure, Procedures } from '@igus/iglidur-free-form-hub-interfaces';
import type { TFunction } from 'next-i18next';
import { EnvironmentName } from '@hub/libs/constants';

export const getIglidurDesignerUrl = ({
  envName,
  locale,
  ifsId,
  quantity,
  article,
}: {
  envName: string;
  locale: string;
  ifsId: string;
  quantity: number | string;
  article: string;
}): string => {
  const baseUrl =
    envName === EnvironmentName.ORANGE
      ? 'https://iglidur-designer.igus.tools'
      : 'https://idd-kopla-integration.igusdev.igus.de';

  return `${baseUrl}/?l=${locale}&fileId=${ifsId}&quantity=${String(quantity)}&article=${article}`;
};

export const isFootnoteVisible = (material: Material, footnoteIndex: number): boolean => {
  if (!material) {
    return false;
  }
  if (footnoteIndex === 4) {
    return material.chemicals?.some(chemical => chemical.resistance === ChemicalResistance.resistantButSwelling);
  }
  if (footnoteIndex === 6 && material.name === 'I6-PL') {
    return true;
  }
  if (material.procedure === Procedures.IM) {
    // For IM we only show the first footnote
    return footnoteIndex === 1;
  }
  // For SLS and FDM we only show 1, 2 and 3
  return [1, 2, 3].includes(footnoteIndex);
};

export type NbsApp = App.ibd | App.ige;

export enum App {
  ibd = 'ibd',
  ige = 'ige',
}

export const categories: Record<NbsApp, string[]> = {
  ibd: ['iglidur_bearings_F', 'iglidur_bearings_FL', 'iglidur_bearings_S', 'iglidur_bearings_T', 'piston_rings'],
  ige: ['gears_spur_straight', 'gears_beveled_straight', 'gear_racks_straight'],
};

export const getNbsAppForCategory = (nbsResponse: NbsResponse): NbsApp => {
  return Object.keys(categories).find((key: NbsApp) => categories[key].includes(nbsResponse.category)) as NbsApp;
};

export const getProcedureText = (materialProcedure: Procedure, t: TFunction): string => {
  const procedureNameSpace = 'free-form-hub/freeFormHubCommon';
  switch (materialProcedure) {
    case Procedures.IM:
      return t('COMMON.PRODUCTION_METHODS.MOLD_INJECTION', { ns: procedureNameSpace });
    case Procedures.CNC:
      return t('COMMON.PRODUCTION_METHODS.CNC', { ns: procedureNameSpace });
    case Procedures.CNC_EXPRESS:
      return t('COMMON.PRODUCTION_METHODS.CNC_EXPRESS', { ns: procedureNameSpace });
    default:
      return `${materialProcedure} ${t('COMMON.PRODUCTION_METHODS.THREE_DIMENSIONAL_PRINT', {
        ns: procedureNameSpace,
      })}`;
  }
};
